// What the site hands /signup (APS-435): with the visitor's consent there, and only then, the
// site's links carry ph_did (PostHog's anonymous id on apsio.io) and the visit's first campaign
// tags and referring host. The console takes them off the address before anything else runs
// (main.tsx), so neither the router nor PostHog ever sees them, and keeps what it uses for this
// tab in session storage, across the identity provider's round trip:
// - the site's id, until the person answers the console's own analytics question: Accept aliases
//   it to their Apsio user id (lib/analytics.ts), Decline drops it;
// - the campaign source and referring host, until their first organization is created, whose
//   signed_up event the API records with them (useCreateOrg).

const KEY = 'apsio.signup'
/** Every parameter the site may add; all of them leave the address. */
const PARAMS = [
  'ph_did',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref',
  'ref_domain',
]
/** PostHog's ids (UUIDs) and nothing that could hold an address or text. */
const SITE_ID = /^[A-Za-z0-9_-]{8,64}$/

export interface SignupHandoff {
  /** PostHog's anonymous id on the site. */
  siteId?: string
  /** utm_source, else the link's ref. */
  utmSource?: string
  referringDomain?: string
}

function read(storage: Storage): SignupHandoff | null {
  try {
    const raw = storage.getItem(KEY)
    return raw ? (JSON.parse(raw) as SignupHandoff) : null
  } catch {
    return null
  }
}

function write(storage: Storage, value: SignupHandoff | null) {
  try {
    if (value && Object.keys(value).length > 0) storage.setItem(KEY, JSON.stringify(value))
    else storage.removeItem(KEY)
  } catch {
    // Blocked storage: the hand-off is lost, the sign-up goes on.
  }
}

/** Takes the site's parameters off /signup's address, keeping the rest, and stores what is used. */
export function takeSignupHandoff(
  location: Location = window.location,
  history: History = window.history,
  storage: Storage = window.sessionStorage,
): void {
  if (location.pathname.replace(/\/+$/, '') !== '/signup') return
  const params = new URLSearchParams(location.search)
  if (!PARAMS.some((p) => params.has(p))) return
  const siteId = params.get('ph_did') ?? ''
  const utmSource = (params.get('utm_source') || params.get('ref') || '').slice(0, 200)
  const referringDomain = (params.get('ref_domain') ?? '').slice(0, 253)
  for (const p of PARAMS) params.delete(p)
  const query = params.toString()
  history.replaceState(
    history.state,
    '',
    `${location.pathname}${query ? `?${query}` : ''}${location.hash}`,
  )
  write(storage, {
    ...(SITE_ID.test(siteId) ? { siteId } : {}),
    ...(utmSource ? { utmSource } : {}),
    ...(referringDomain ? { referringDomain } : {}),
  })
}

export function signupHandoff(storage: Storage = window.sessionStorage): SignupHandoff | null {
  return read(storage)
}

/** The site's id is used (aliased) or refused: it goes, the origin stays for the organization. */
export function forgetSiteId(storage: Storage = window.sessionStorage): void {
  const current = read(storage)
  if (!current?.siteId) return
  const { siteId: _, ...rest } = current
  write(storage, rest)
}

/** The first organization recorded the origin, or the person signed out: nothing is kept. */
export function clearSignupHandoff(storage: Storage = window.sessionStorage): void {
  write(storage, null)
}
